import { createAction, createReducer } from 'redux-act';
import { useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { CategoryFilter, OfferItems } from '../../components/offers.new/types';
import type { UserPredictions } from '../../services/offers/offers.types';
import { CREDIT_SECURITY_VALUES } from '../../constants/sources';
import { ArrangeItem } from '../../helpers/client/arrange';
import type { SelectionDictionaries } from '../../services/offers/dictionaries';
import { GatewayV2Types } from '../../services/gatewayV2/gatewayV2.types';

interface IState {
    isSuccessResponse: boolean;
    initialized: boolean;
    calcId: string;
    searchId?: string;
    availableBanks: Array<string>;
    userPredictions: UserPredictions;
    items: OfferItems;
    filters: {
        categoryFilters: Array<CategoryFilter>;
        creditSecurityFilterValue: ValuesOf<typeof CREDIT_SECURITY_VALUES>;
    };
    /** CF-1222 */
    isTinkoffBannerCanceledManually: boolean;
    /** CF-1560 */
    arrange: Record<string, ArrangeItem>;
    /** CF-1786 */
    groups: Array<SelectionDictionaries.TGroup>;
    /** CF-3545 */
    isBentoRankingModel?: boolean;
}

const defaultState: IState = {
    isSuccessResponse: false,
    initialized: false,
    isBentoRankingModel: false,
    calcId: '',
    searchId: '',
    userPredictions: [],
    availableBanks: [],
    items: {
        suitable: [],
        recommended: [],
        final: [],
        unsuitable: [],
        decisionOffers: [],
    },
    filters: {
        categoryFilters: [],
        creditSecurityFilterValue: CREDIT_SECURITY_VALUES.DOES_NOT_MATTER,
    },
    isTinkoffBannerCanceledManually: false,
    arrange: {},
    groups: [],
};

export const setOffersData = createAction<Partial<IState>>('set offers data');

export const setGroups = createAction<IState['groups']>('set offers groups');
export const setAvailableBanks = createAction<IState['availableBanks']>('set available banks');

/** Позволяет обновить одну группу. Обновляется или добавляется в groups */
export const updateGroup = createAction<SelectionDictionaries.TGroup>('update group');

export const updateGroupPreApprovalOffers = createAction<{
    type: SelectionDictionaries.TGroup['type'];
    preapprovalData: GatewayV2Types.StatusesV2Response['preApprovalResult'];
}>('update group preApprovalOffers');

export const setFilters = createAction<Partial<IState['filters']>>('set filters');

export const setDecisionOffers = createAction<IState['items']['decisionOffers']>('set decisionOffers');

/** Данные, связанные с компонентом выдачи кредита */
export const offerReducer = createReducer({}, defaultState)
    .on(setFilters, (state, payload) => {
        return {
            ...state,
            filters: {
                ...state.filters,
                ...payload,
            },
        };
    })
    .on(setDecisionOffers, (state, payload) => {
        return {
            ...state,
            items: {
                ...state.items,
                decisionOffers: payload,
            },
        };
    })
    .on(setAvailableBanks, (state, payload) => {
        return {
            ...state,
            availableBanks: payload,
        };
    })
    .on(setOffersData, (state, payload) => {
        return {
            ...state,
            ...payload,
        };
    })
    .on(setGroups, (state, payload) => {
        return {
            ...state,
            groups: payload,
        };
    })
    .on(updateGroup, (state, payload) => {
        const groupIndex = state.groups.findIndex(({ type }) => type === payload.type);
        if (groupIndex !== -1) {
            state.groups[groupIndex] = payload;
        } else {
            state.groups.push(payload);
        }
        return { ...state, groups: [...state.groups] };
    })
    .on(updateGroupPreApprovalOffers, (state, payload) => {
        const { preapprovalData } = payload;
        const groupIndex = state.groups.findIndex(({ type }) => type === payload.type);

        const { items = [], status } = preapprovalData || {};
        const preApprovalResultMap = new Map(items.map((item) => [item.productId, item]));

        const failedPreApprovalOffers = items
            .filter(
                (result) =>
                    // если по офферу пришел статус FAILED
                    result.offerView?.groupItem?.preApprovalStatus === 'FAILED' ||
                    // или если общий статус поллинга COMPLETE, но данные по офферу так и не пришли
                    (status === 'complete' && result.offerView?.groupItem?.isPreapproving),
            )
            .map((result) => result.productId);

        state.groups[groupIndex].items = state.groups[groupIndex].items
            .filter((offer) => !failedPreApprovalOffers.includes(offer.id))
            .map((offer) => {
                if (offer.id && preApprovalResultMap.has(offer.id)) {
                    const offerResult = preApprovalResultMap.get(offer.id) as (typeof preapprovalData)['items'][0];
                    if (offerResult) {
                        const { offerView } = offerResult || {};
                        const { groupItem } = offerView || {};
                        const { summaries, isPreapproving, buttonLink, badges, preApprovalOffer } = groupItem || {};

                        return {
                            ...offer,
                            badges: badges || offer.badges,
                            summaries: (summaries
                                ? summaries.map((summary) => ({
                                      label: summary.label?.toString() || '',
                                      text: summary.text?.toString() || '',
                                      value: summary.value?.toString() || '',
                                  }))
                                : offer.summaries) as SelectionDictionaries.TGroupItem['summaries'],
                            isPreapproving,
                            buttonLink,
                            referralLink: buttonLink,
                            preApprovalOfferId: preApprovalOffer?.id,
                        };
                    }
                }
                return offer;
            })
            // если общий статус поллинга COMPLETE, но данные по офферу так и не пришли
            .filter((offer) => {
                if (status === 'complete') {
                    return !offer.isPreapproving;
                }
                return true;
            });
        return { ...state, groups: [...state.groups] };
    });

export const useOffers = () => useSelector((state: { offers: IState }) => state.offers);

export const useDecisionOffers = () => useSelector((state: { offers: IState }) => state.offers.items.decisionOffers);

export const hasPreapprovalsInProgressSelector =
    (groupType: SelectionDictionaries.TGroup['type']) => (state: { offers: IState }) => {
        const groupIndex = state.offers.groups.findIndex(({ type }) => type === groupType);
        if (groupIndex === -1) {
            return false;
        }
        return state.offers.groups[groupIndex].items.some((item) => item.isPreapproving);
    };

/**
 * Возвращает все группы из ответа products
 * !!! Если нужны определенные группы, используй usePickOffersGroups()
 */
export const useOffersGroups = () => useSelector((state: { offers: IState }) => state.offers.groups);

type CalculateAvailableBankThunkProps = {
    successCallback: (filteredAvailableBanksCount: number) => Promise<any>;
    ids: Array<string>;
};
export const calculateAvailableBankThunk =
    ({ successCallback, ids }: CalculateAvailableBankThunkProps) =>
    async (dispatch: Dispatch, getState: () => { offers: IState }) => {
        const { availableBanks } = getState().offers;
        const filteredAvailableBanks = availableBanks.filter((bankId) => !ids.includes(bankId));
        if (filteredAvailableBanks.length !== availableBanks.length) {
            await dispatch(setAvailableBanks(filteredAvailableBanks));
        }
        const data = await successCallback(filteredAvailableBanks.length);

        return data;
    };
